/**
 * LYT Communications - Map Data Service
 * Version: 7.0.0
 * Updated: 2026-02-27
 *
 * Data bridge between React frontend and backend.
 * Map Tracer data (projects, segments, devices, line items, assignments) → Vercel Postgres via /api/ routes
 * Other data (rate cards, issues, locates, invoices, damage reports, work log) → Google Sheets via Gateway
 *
 * All exported function signatures are identical to v6.0.0 — no component changes needed.
 */

import { STATUS_COLORS } from '../config/mapConfig';

// ===== API CONFIGURATION =====
// Postgres-backed routes (same origin on Vercel)
const API_BASE = '/api';

// Google Sheets Gateway (for non-migrated data)
const GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbyFWHLgFOglJ75Y6AGnyme0P00OjFgE_-qrDN9m0spn4HCgcyBpjvMopsB1_l9MDjIctQ/exec';
const GATEWAY_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';

// Database spreadsheet IDs (for Sheets-only data)
const MAP_SS = '1tW_3y6OzEMmkPX8JiYIN6m6dgwx291RSwQ2uTN5X8sg';
const DB = {
  PROJECTS:     MAP_SS,
  SEGMENTS:     MAP_SS,
  DEVICES:      MAP_SS,
  LINE_ITEMS:   MAP_SS,
  ASSIGNMENTS:  '1g2Ml8PFsN0HZA_chLqje2OTnYAZXDPVMZ1trRmKN-qY',
  RATE_CARDS:   '10Py5x0vIUWPzKn1ZeTaIGyaEJonbz-0BHmSYV-20rB4',
  USERS:        '1OjSak2YJJvbXjyX3FSND_GfaQUZ2IQkFiMRgLuNfqVw',
  WORK_LOG:     '1mhO4eZ-07SWM2VOjHcZML7vne9dMyT33O0bSI1DzcC8',
  ISSUES:       '1hPth_lqawUJfX5ik2dROL7j96v1j1i3FA1kkdgqk83g',
};

const TAB = { PROJECTS: 'Projects', SEGMENTS: 'Segments', DEVICES: 'Devices', LINE_ITEMS: 'LineItems' };

// Legacy tab names (kept for Sheets-only functions and migration reference)
const SEGMENT_TABS = ['UG1','UG2','UG3','UG16','UG21','UG23','UG24','UG29','UG30','MISC_SEG'];
const DEVICE_TABS = ['UG10','UG11','UG12','UG13','UG14','UG15','UG17','UG18','UG19','UG20','UG27','UG31','FS1','MISC_DEV'];
const ALL_BILLING_TABS = [...SEGMENT_TABS, ...DEVICE_TABS];

// Handhole size → unit code mapping
const HANDHOLE_UNIT_MAP = {
  '15x20x12': 'UG20',
  '17x30x18': 'UG17',
  '24x36x18': 'UG18',
  '24x36x24': 'UG11',
  '30x48x18': 'UG19',
  '30x48x24': 'UG27',
  '30x48x30': 'UG10',
};

// Device headers (for legacy Sheets operations)
const DEVICES_HEADERS = ['device_id','project_id','device_type','contractor_id','unit_code','qty','gps_lat','gps_lng','size','splice_type','handhole_id','fiber_count','tray_count','status','notes','timestamp'];

// ===== EMPTY FALLBACK DATA =====
const DEMO_PROJECT = { project_id: 'OFFLINE', name: 'Database Offline', client: '', status: 'Offline' };
const DEMO_SEGMENTS = [];

// ===== FULL RATE CARDS (client rates — server-side, key-gated) =====
// Client billing rates are NOT in the JS bundle. Admin pages fetch them from
// /api/rates with the x-rates-key header. The key is entered once and cached
// in localStorage. Contractors never receive these rates.
const RATES_KEY_STORAGE = 'lyt-rates-key';
const _fullRatesCache = {};

export async function fetchFullRates(rateCardId, { promptForKey = true } = {}) {
  const cacheKey = rateCardId || 'default';
  if (_fullRatesCache[cacheKey]) return _fullRatesCache[cacheKey];

  let key = (localStorage.getItem(RATES_KEY_STORAGE) || '').trim();
  for (let attempt = 0; attempt < 2; attempt++) {
    if (!key) {
      if (!promptForKey) return null;
      key = (window.prompt('Enter the LYT rates access key (admin only):') || '').trim();
      if (!key) return null;
      localStorage.setItem(RATES_KEY_STORAGE, key);
    }
    const resp = await fetch(`${API_BASE}/rates?rateCard=${encodeURIComponent(rateCardId || '')}`, {
      headers: { 'x-rates-key': key },
    });
    if (resp.status === 401) {
      localStorage.removeItem(RATES_KEY_STORAGE);
      key = '';
      continue; // re-prompt once
    }
    if (!resp.ok) throw new Error(`Rates fetch failed: ${resp.status}`);
    const json = await resp.json();
    if (!json.success) throw new Error(json.error || 'Rates fetch failed');
    _fullRatesCache[cacheKey] = json.data; // { rates, priceField } — priceField is 'client_rate' or 'vexus'
    return json.data;
  }
  return null;
}

// ===== API HELPERS =====

async function apiGet(path) {
  try {
    const resp = await fetch(`${API_BASE}${path}`);
    const json = await resp.json();
    if (json.success) return json.data || [];
    console.error(`[API GET ${path}]`, json.error);
    return [];
  } catch (err) {
    console.error(`[API GET ${path}]`, err);
    return [];
  }
}

async function apiPost(path, body) {
  try {
    const resp = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return await resp.json();
  } catch (err) {
    console.error(`[API POST ${path}]`, err);
    return { success: false, error: err.message };
  }
}

async function apiPatch(path, body) {
  try {
    const resp = await fetch(`${API_BASE}${path}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return await resp.json();
  } catch (err) {
    console.error(`[API PATCH ${path}]`, err);
    return { success: false, error: err.message };
  }
}

// ===== GOOGLE SHEETS GATEWAY (for non-migrated data) =====

async function gatewayCall(action, params) {
  try {
    const bodyStr = JSON.stringify({ secret: GATEWAY_SECRET, action, params });
    console.log(`[gatewayCall] ${action} (${Math.round(bodyStr.length / 1024)}KB)`);
    const response = await fetch(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: bodyStr,
    });
    const text = await response.text();
    if (text.includes('<HTML>')) {
      const match = text.match(/HREF="([^"]+)"/);
      if (match) {
        const redirectUrl = match[1].replace(/&amp;/g, '&');
        const finalResp = await fetch(redirectUrl);
        const finalText = await finalResp.text();
        try {
          return JSON.parse(finalText);
        } catch (parseErr) {
          console.error(`[gatewayCall] ${action} — JSON parse error on redirect response:`, finalText.slice(0, 300));
          return { success: false, error: `Parse error: ${finalText.slice(0, 100)}` };
        }
      }
      console.error(`[gatewayCall] ${action} — redirect HTML but no HREF found:`, text.slice(0, 300));
      return { success: false, error: 'Redirect without HREF' };
    }
    try {
      return JSON.parse(text);
    } catch (parseErr) {
      console.error(`[gatewayCall] ${action} — JSON parse error:`, text.slice(0, 300));
      return { success: false, error: `Parse error: ${text.slice(0, 100)}` };
    }
  } catch (err) {
    console.error(`[gatewayCall] ${action} — fetch failed:`, err);
    return { success: false, error: err.message };
  }
}

export async function readSheet(spreadsheetId, range) {
  const result = await gatewayCall('sheetsRead', { spreadsheetId, range });
  if (result.success && result.data && result.data.data) {
    return result.data.data;
  }
  return [];
}

export async function writeSheet(spreadsheetId, range, values) {
  console.log(`[writeSheet] Writing ${values.length} rows to ${range}`);
  const result = await gatewayCall('sheetsWrite', { spreadsheetId, range, values });
  if (!result.success) {
    const errDetail = result.error || result.message || JSON.stringify(result).slice(0, 200);
    console.error(`[writeSheet] FAILED for ${range}:`, errDetail);
    writeSheet._lastError = errDetail;
  }
  return result.success || false;
}
writeSheet._lastError = '';

export async function appendRow(spreadsheetId, range, values) {
  const result = await gatewayCall('sheetsAppend', { spreadsheetId, range, values });
  return result.success || false;
}

// ===== ROW PARSING (for Sheets-only data) =====

function rowsToObjects(rows) {
  if (!rows || rows.length < 2) return [];
  const headers = rows[0].map(h => (h || '').toString().trim());
  return rows.slice(1)
    .filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined))
    .map(row => {
      const obj = {};
      headers.forEach((h, i) => {
        if (h) obj[h] = row[i] !== undefined && row[i] !== null ? row[i] : '';
      });
      return obj;
    });
}

function colToLetter(colIndex) {
  if (colIndex < 26) return String.fromCharCode(65 + colIndex);
  return String.fromCharCode(64 + Math.floor(colIndex / 26)) + String.fromCharCode(65 + (colIndex % 26));
}

// ===== SEGMENT NORMALIZATION =====

function normalizeSegments(segments) {
  return segments.map(s => ({
    ...s,
    footage: Math.round((parseFloat(s.footage) || 0) * 10) / 10,
    gps_start: s.gps_start || { lat: parseFloat(s.gps_start_lat) || 0, lng: parseFloat(s.gps_start_lng) || 0 },
    gps_end: s.gps_end || { lat: parseFloat(s.gps_end_lat) || 0, lng: parseFloat(s.gps_end_lng) || 0 },
    boring_status: s.boring_status || 'Not Started',
    pulling_status: s.pulling_status || 'Not Started',
    splicing_status: s.splicing_status || 'Not Started',
    boring_assigned_to: s.boring_assigned_to || '',
    pulling_assigned_to: s.pulling_assigned_to || '',
    splicing_assigned_to: s.splicing_assigned_to || '',
  }));
}

// ===== DATABASE CONNECTIVITY =====

let _dbOnline = null;
let _dbCheckedAt = 0;

export async function checkDbConnection() {
  const now = Date.now();
  const staleMs = _dbOnline ? 300000 : 30000;
  if (_dbOnline !== null && (now - _dbCheckedAt) < staleMs) return _dbOnline;
  try {
    // Ping Postgres via the projects API
    const resp = await fetch(`${API_BASE}/projects`);
    const json = await resp.json();
    _dbOnline = json.success === true;
    _dbCheckedAt = now;
    console.log(`[mapService] DB ${_dbOnline ? 'ONLINE (Postgres)' : 'OFFLINE'}`);
    return _dbOnline;
  } catch (e) {
    _dbOnline = false;
    _dbCheckedAt = now;
    console.log('[mapService] DB OFFLINE - demo fallback');
    return false;
  }
}

export function isDemoMode() {
  return _dbOnline === false;
}

export function resetDbCache() {
  _dbOnline = null;
  _dbCheckedAt = 0;
}

// ===== DATA LOADING (Postgres-backed) =====

export async function loadProjects(projectId) {
  const online = await checkDbConnection();
  if (!online) {
    const projects = [DEMO_PROJECT];
    return projectId ? projects.filter(p => p.project_id === projectId) : projects;
  }
  try {
    const path = projectId ? `/projects?project_id=${encodeURIComponent(projectId)}` : '/projects';
    return await apiGet(path);
  } catch (err) {
    console.error('[mapService] loadProjects failed:', err);
    return [DEMO_PROJECT];
  }
}

export async function loadSections(projectId, contractorCompany) {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    let url = `/sections?project_id=${encodeURIComponent(projectId)}`;
    if (contractorCompany) url += `&contractor=${encodeURIComponent(contractorCompany)}`;
    return await apiGet(url);
  } catch (err) {
    console.error('[mapService] loadSections failed:', err);
    return [];
  }
}

export async function loadSegments(projectId, section, { lytAssigned } = {}) {
  const online = await checkDbConnection();
  if (!online) return normalizeSegments(DEMO_SEGMENTS);
  try {
    const params = [];
    if (projectId) params.push(`project_id=${encodeURIComponent(projectId)}`);
    if (section) params.push(`section=${encodeURIComponent(section)}`);
    if (lytAssigned) params.push('lyt_assigned=true');
    const path = params.length > 0 ? `/segments?${params.join('&')}` : '/segments';
    const rows = await apiGet(path);
    return normalizeSegments(rows);
  } catch (err) {
    console.error('[mapService] loadSegments failed:', err);
    return normalizeSegments(DEMO_SEGMENTS);
  }
}

export async function loadDevices(projectId, section, { lytAssigned } = {}) {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    const params = [];
    if (projectId) params.push(`project_id=${encodeURIComponent(projectId)}`);
    if (section) params.push(`section=${encodeURIComponent(section)}`);
    if (lytAssigned) params.push('lyt_assigned=true');
    const path = `/devices?${params.join('&')}`;
    return await apiGet(path);
  } catch (err) {
    console.error('[mapService] loadDevices failed:', err);
    return [];
  }
}

export async function loadAssignments(projectId) {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    const path = projectId ? `/assignments?project_id=${encodeURIComponent(projectId)}` : '/assignments';
    return await apiGet(path);
  } catch (err) {
    console.error('[mapService] loadAssignments failed:', err);
    return [];
  }
}

export async function loadLineItems(projectId) {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    return await apiGet(`/line-items?project_id=${encodeURIComponent(projectId)}`);
  } catch (err) {
    console.error('[mapService] loadLineItems failed:', err);
    return [];
  }
}

/**
 * Load Vexus pay app invoices (parsed from Drive PDFs).
 * Loads ALL invoices since client project IDs may differ from internal ones.
 */
export async function loadEmailInvoices() {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    return await apiGet('/invoice-import');
  } catch (err) {
    console.error('[mapService] loadEmailInvoices failed:', err);
    return [];
  }
}

/**
 * Scan Google Drive invoice folder for new PDFs and parse them.
 */
export async function scanDriveInvoices(projectId) {
  const online = await checkDbConnection();
  if (!online) return { success: false, error: 'Offline' };
  try {
    return await apiPost('/invoice-import', { project_id: projectId });
  } catch (err) {
    console.error('[mapService] scanDriveInvoices failed:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Upload a PDF invoice for parsing via Claude Vision.
 */
export async function uploadInvoicePDF(projectId, pdfBase64, filename) {
  const online = await checkDbConnection();
  if (!online) return { success: false, error: 'Offline' };
  try {
    return await apiPost('/invoice-import', { project_id: projectId, pdf_base64: pdfBase64, filename });
  } catch (err) {
    console.error('[mapService] uploadInvoicePDF failed:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Load all data for a project in one call.
 * Returns { project, segments, splicePoints, handholes, flowerpots, groundRods, assignments, isDemo }
 */
export async function loadFullProject(projectId, section) {
  const online = await checkDbConnection();
  const isDemo = !online;
  if (isDemo) console.log('[mapService] Demo mode - using built-in data');
  else console.log(`[mapService] LIVE mode - loading from Postgres${section ? ` (section: ${section})` : ''}`);

  const [projects, segments, devices, assignments] = await Promise.all([
    loadProjects(projectId),
    loadSegments(projectId, section),
    loadDevices(projectId, section),
    loadAssignments(projectId),
  ]);

  // Split unified devices into typed arrays for backward compatibility
  const deviceFields = d => ({
    vendor_codes: d.vendor_codes, crew_name: d.crew_name, work_order_name: d.work_order_name, section: d.section,
  });
  const handholes = devices.filter(d => d.device_type === 'handhole').map(d => ({
    device_id: d.device_id, handhole_id: d.device_id, project_id: d.project_id, contractor_id: d.contractor_id,
    size: d.size, unit_code: d.unit_code, qty: d.qty, gps_lat: d.gps_lat, gps_lng: d.gps_lng,
    status: d.status, notes: d.notes, timestamp: d.created_at, ...deviceFields(d),
  }));
  const flowerpots = devices.filter(d => d.device_type === 'flowerpot').map(d => ({
    device_id: d.device_id, flowerpot_id: d.device_id, project_id: d.project_id, contractor_id: d.contractor_id,
    unit_code: d.unit_code, qty: d.qty, gps_lat: d.gps_lat, gps_lng: d.gps_lng,
    status: d.status, timestamp: d.created_at, ...deviceFields(d),
  }));
  const pedestals = devices.filter(d => d.device_type === 'pedestal').map(d => ({
    device_id: d.device_id, project_id: d.project_id, contractor_id: d.contractor_id,
    unit_code: d.unit_code, qty: d.qty, gps_lat: d.gps_lat, gps_lng: d.gps_lng,
    status: d.status, notes: d.notes, timestamp: d.created_at, ...deviceFields(d),
  }));
  const cabinets = devices.filter(d => d.device_type === 'cabinet').map(d => ({
    device_id: d.device_id, project_id: d.project_id, contractor_id: d.contractor_id,
    unit_code: d.unit_code, qty: d.qty, gps_lat: d.gps_lat, gps_lng: d.gps_lng,
    status: d.status, notes: d.notes, timestamp: d.created_at, ...deviceFields(d),
  }));
  const splicePoints = devices.filter(d =>
    d.device_type === 'splice_point' || d.device_type === 'splitter_1x4' || d.device_type === 'splitter_2x8'
  ).map(d => ({
    device_id: d.device_id, splice_id: d.device_id, project_id: d.project_id, contractor_id: d.contractor_id,
    handhole_id: d.handhole_id, splice_type: d.splice_type, fiber_count: d.fiber_count,
    tray_count: d.tray_count, gps_lat: d.gps_lat, gps_lng: d.gps_lng,
    status: d.status, notes: d.notes, unit_code: d.unit_code, ...deviceFields(d),
  }));
  const groundRods = devices.filter(d => d.device_type === 'ground_rod').map(d => ({
    device_id: d.device_id, ground_rod_id: d.device_id, project_id: d.project_id,
    unit_code: d.unit_code, qty: d.qty, notes: d.notes, timestamp: d.created_at,
  }));
  const fats = devices.filter(d => d.device_type === 'fat').map(d => ({
    device_id: d.device_id, project_id: d.project_id, contractor_id: d.contractor_id,
    unit_code: d.unit_code, qty: d.qty, gps_lat: d.gps_lat, gps_lng: d.gps_lng,
    status: d.status, notes: d.notes, timestamp: d.created_at, ...deviceFields(d),
  }));

  return {
    project: projects[0] || DEMO_PROJECT,
    segments,
    splicePoints,
    handholes,
    flowerpots,
    pedestals,
    cabinets,
    groundRods,
    fats,
    assignments,
    isDemo,
  };
}

export async function loadFullProjectWithLineItems(projectId, section) {
  const base = await loadFullProject(projectId, section);
  let lineItems = await loadLineItems(projectId);

  // Auto-generate pulling line items for any segment that doesn't have one
  if (base.segments && base.segments.length > 0 && !base.isDemo) {
    const rateCard = base.project?.rate_card || 'vexus-la-tx-2026';
    const generated = await syncPullingLineItems(projectId, base.segments, lineItems, rateCard);
    if (generated > 0) {
      lineItems = await loadLineItems(projectId);
    }
  }

  return { ...base, lineItems };
}

/**
 * Ensure every segment has a pulling line item (UG4 or UG28).
 * Only creates missing items — never overwrites existing ones.
 * Returns count of newly created items.
 */
async function syncPullingLineItems(projectId, segments, existingLineItems, rateCardId) {
  // Build set of segment_ids that already have a pulling line item
  const hasPulling = new Set();
  for (const li of existingLineItems) {
    if (li.segment_id && (li.code === 'UG4' || li.code === 'UG28' || li.code === 'UG22')) {
      hasPulling.add(li.segment_id);
    }
  }

  const missing = [];
  for (const seg of segments) {
    const segId = seg.segment_id;
    if (!segId || hasPulling.has(segId)) continue;

    const footage = parseFloat(seg.pull_footage || seg.footage) || 0;
    if (footage <= 0) continue;

    // Determine pulling code from cable_type
    const cableType = (seg.cable_type || seg.pulling_cable_type || '').toLowerCase();
    const isLargeCount = cableType.includes('288') || cableType.includes('432') || cableType.includes('armored');
    const code = isLargeCount ? 'UG28' : 'UG4';
    const vexusRate = code === 'UG28' ? 1.00 : 0.55;
    const contractorRate = code === 'UG28' ? 0.60 : 0.40;
    const description = code === 'UG28' ? 'Pull 288-432ct armored fiber in duct' : 'Pull up to 144ct armored/micro cable';

    missing.push({
      line_item_id: `${segId}-PULL`,
      project_id: projectId,
      code,
      description,
      uom: 'LF',
      quantity: footage,
      segment_id: segId,
      vexus_rate: vexusRate,
      contractor_rate: contractorRate,
      margin: parseFloat((vexusRate - contractorRate).toFixed(2)),
      status: 'Planned',
    });
  }

  if (missing.length === 0) return 0;

  try {
    console.log(`[mapService] Creating ${missing.length} pulling line items for ${projectId}`);
    await apiPost('/line-items', { line_items: missing });
    return missing.length;
  } catch (err) {
    console.error('[mapService] syncPullingLineItems failed:', err);
    return 0;
  }
}

// ===== DATA WRITING (Postgres-backed) =====

export async function updateSegmentField(segmentId, field, value) {
  const online = await checkDbConnection();
  if (!online) {
    console.log(`[mapService] Demo: ${segmentId}.${field} = ${value}`);
    return true;
  }
  try {
    const result = await apiPatch('/segments', { segment_id: segmentId, field, value });
    return result.success || false;
  } catch (err) {
    console.error('[mapService] updateSegmentField failed:', err);
    return false;
  }
}

export async function updateDeviceField(deviceId, field, value) {
  const online = await checkDbConnection();
  if (!online) {
    console.log(`[mapService] Demo: ${deviceId}.${field} = ${value}`);
    return true;
  }
  try {
    const result = await apiPatch('/devices', { device_id: deviceId, field, value });
    return result.success || false;
  } catch (err) {
    console.error('[mapService] updateDeviceField failed:', err);
    return false;
  }
}

export async function updateSpliceField(spliceId, field, value) {
  return updateDeviceField(spliceId, field, value);
}

export async function updateItemStatus(itemType, itemId, projectId, field, newValue, actorName, actorPhone, photoUrls) {
  try {
    return await apiPost('/status-changes', {
      item_type: itemType,
      item_id: itemId,
      project_id: projectId,
      field,
      new_value: newValue,
      actor_name: actorName || null,
      actor_phone: actorPhone || null,
      photo_urls: photoUrls ? JSON.stringify(photoUrls) : null,
    });
  } catch (err) {
    console.error('[mapService] updateItemStatus failed:', err);
    return { success: false, error: err.message };
  }
}

export async function loadStatusHistory(itemType, itemId) {
  try {
    return await apiGet(`/status-changes?item_type=${encodeURIComponent(itemType)}&item_id=${encodeURIComponent(itemId)}`);
  } catch (err) {
    console.error('[mapService] loadStatusHistory failed:', err);
    return [];
  }
}

export async function updateAssignmentField(assignmentId, field, value) {
  const online = await checkDbConnection();
  if (!online) return false;
  try {
    const result = await apiPatch('/assignments', { assignment_id: assignmentId, field, value });
    return result.success || false;
  } catch (err) {
    console.error('[mapService] updateAssignmentField failed:', err);
    return false;
  }
}

// ===== ASSIGNMENT WORKFLOW =====

export async function acceptAssignment(assignmentId, context = {}) {
  const now = new Date().toISOString();
  const ok1 = await updateAssignmentField(assignmentId, 'contractor_accepted', true);
  const ok2 = await updateAssignmentField(assignmentId, 'contractor_accepted_at', now);
  const ok3 = await updateAssignmentField(assignmentId, 'status', 'Accepted');

  // Notify admins (C2: WORKFLOW Step 67)
  try {
    const { sendEmailNotification, addNotification } = await import('./notificationService');
    const name = context.contractorName || 'A contractor';
    const type = context.workType || 'work';
    const count = context.segmentCount || '';
    const admins = ['matt@lytcomm.com', 'mason@lytcomm.com', 'donnie@lytcomm.com'];
    for (const admin of admins) {
      await sendEmailNotification(admin, `Assignment Accepted — ${name}`,
        `<p><strong>${name}</strong> accepted <strong>${type}</strong> assignment${count ? ` (${count} segments)` : ''}.</p>
         <p><a href="https://lytcomm.com/#admin-projects">View in Admin Dashboard</a></p>`);
    }
    addNotification({ type: 'assignment_accepted', severity: 'info',
      title: 'Assignment Accepted', message: `${name} accepted ${type}` });
  } catch (err) {
    console.warn('[mapService] Accept notification failed:', err);
  }

  return ok1 && ok2 && ok3;
}

export async function declineAssignment(assignmentId, reason, context = {}) {
  const ok1 = await updateAssignmentField(assignmentId, 'contractor_accepted', false);
  const ok2 = await updateAssignmentField(assignmentId, 'status', 'Declined');
  const ok3 = await updateAssignmentField(assignmentId, 'declined_reason', reason || '');

  // Notify admins (C2: WORKFLOW Step 67)
  try {
    const { sendEmailNotification, addNotification } = await import('./notificationService');
    const name = context.contractorName || 'A contractor';
    const type = context.workType || 'work';
    const admins = ['matt@lytcomm.com', 'mason@lytcomm.com', 'donnie@lytcomm.com'];
    for (const admin of admins) {
      await sendEmailNotification(admin, `Assignment Declined — ${name}`,
        `<p><strong>${name}</strong> declined <strong>${type}</strong> assignment.</p>
         ${reason ? `<p><strong>Reason:</strong> ${reason}</p>` : ''}
         <p><a href="https://lytcomm.com/#admin-projects">View in Admin Dashboard</a></p>`);
    }
    addNotification({ type: 'assignment_declined', severity: 'warning',
      title: 'Assignment Declined', message: `${name} declined ${type}${reason ? ': ' + reason : ''}` });
  } catch (err) {
    console.warn('[mapService] Decline notification failed:', err);
  }

  return ok1 && ok2 && ok3;
}

export async function assignToSub(assignmentId, subEmail) {
  const ok1 = await updateAssignmentField(assignmentId, 'assigned_to_sub', subEmail);
  const ok2 = await updateAssignmentField(assignmentId, 'status', 'Assigned to Sub');
  return ok1 && ok2;
}

/**
 * Bulk-update the `section` field on segments and devices.
 * Batches 10 at a time to avoid flooding the API.
 */
export async function bulkUpdateSection(segmentIds, deviceIds, sectionName) {
  const BATCH_SIZE = 10;
  let segmentsUpdated = 0;
  let devicesUpdated = 0;
  const errors = [];

  // Process segments in batches
  for (let i = 0; i < segmentIds.length; i += BATCH_SIZE) {
    const batch = segmentIds.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(
      batch.map(id => updateSegmentField(id, 'section', sectionName).catch(err => {
        errors.push(`seg ${id}: ${err.message}`);
        return false;
      }))
    );
    segmentsUpdated += results.filter(Boolean).length;
  }

  // Process devices in batches
  for (let i = 0; i < deviceIds.length; i += BATCH_SIZE) {
    const batch = deviceIds.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(
      batch.map(id => updateDeviceField(id, 'section', sectionName).catch(err => {
        errors.push(`dev ${id}: ${err.message}`);
        return false;
      }))
    );
    devicesUpdated += results.filter(Boolean).length;
  }

  return { segmentsUpdated, devicesUpdated, errors };
}

/**
 * Create a sub-assignment record.
 * Creates an assignment with assigned_to_sub set, without changing segment ownership fields.
 */
export async function createSubAssignment({ projectId, segmentIds, subEmail, subName, contractorEmail, contractorName, sections, workType }) {
  const assignmentId = `SUB-ASGN-${Date.now()}`;
  const result = await apiPost('/assignments', {
    assignment_id: assignmentId,
    project_id: projectId || '',
    segment_id: segmentIds[0] || null,
    segment_ids: JSON.stringify(segmentIds),
    contractor_email: contractorEmail,
    contractor_name: contractorName,
    assigned_by: contractorEmail,
    assigned_to_sub: subEmail,
    phase: workType || 'boring',
    work_type: workType || 'boring',
    status: 'Pending',
    notes: `Sections: ${(sections || []).join(', ')}. Assigned to sub: ${subName || subEmail}`,
  });

  if (!result.success) {
    return { success: false, error: result.error };
  }

  return { success: true, assignmentId };
}

export async function subAcceptAssignment(assignmentId) {
  const ok1 = await updateAssignmentField(assignmentId, 'status', 'In Progress');
  return ok1;
}

export function getContractorAssignments(assignments, contractorName) {
  if (!contractorName) return [];
  const lc = contractorName.toLowerCase();
  return assignments.filter(a => (a.contractor_name || '').toLowerCase().includes(lc));
}

export function getSubAssignments(assignments, subEmail) {
  if (!subEmail) return [];
  return assignments.filter(a => a.assigned_to_sub === subEmail);
}

// ===== PUBLISH MAP TRACER DATA (Postgres-backed) =====

/**
 * Publish Map Tracer drawing data to Postgres.
 * Transforms tracer segments/devices into DB format and sends to /api/publish-tracer.
 */
export async function publishTracerData(projectId, tracerSegments, tracerDevices) {
  const online = await checkDbConnection();
  if (!online) return { success: false, error: 'Database offline' };

  const errors = [];

  // Transform tracer segments to DB format
  const segments = (tracerSegments || [])
    .filter(seg => seg.lineType === 'conduit' || seg.lineType === 'fiber' || !seg.lineType)
    .map((seg, i) => {
      const pts = seg.points || [];
      const startPt = pts[0] || {};
      const endPt = pts[pts.length - 1] || {};
      const spec = seg.conduitSpec || '2x1.25';
      const ductMatch = spec.match(/^(\d+)x/);
      const ductCount = ductMatch ? parseInt(ductMatch[1]) : 1;
      const boreCode = seg.lineType === 'fiber' ? '' : (ductCount <= 3 ? `UG${ductCount}` : 'UG16');
      const footage = seg.footage || seg.length || 0;

      return {
        segment_id: seg.id || `${projectId}-SEG-${i + 1}`,
        contractor_id: '',
        section: spec,
        from_structure: seg.startSnap?.deviceId || null,
        to_structure: seg.endSnap?.deviceId || null,
        footage,
        street: seg.street || null,
        gps_start_lat: startPt.lat || null,
        gps_start_lng: startPt.lng || null,
        gps_end_lat: endPt.lat || null,
        gps_end_lng: endPt.lng || null,
        bore_code: boreCode || null,
        bore_footage: footage,
        duct_count: ductCount,
        pull_code: 'UG4',
        pull_footage: footage,
        cable_type: seg.cableType || seg.fiberSpec || null,
      };
    });

  // Transform tracer devices to DB format
  const devices = (tracerDevices || []).map((d, i) => {
    const lat = d.position?.lat || d.lat || null;
    const lng = d.position?.lng || d.lng || null;
    const hhSize = d.subtype || d.size || d.subType || '';
    const unitCode = d.unitCode || (d.type === 'handhole' ? (HANDHOLE_UNIT_MAP[hhSize] || 'UG17') :
      d.type === 'flowerpot' ? 'UG12' :
      d.type === 'ground_rod' ? 'UG13' : null);

    return {
      device_id: d.id || `${projectId}-DEV-${i + 1}`,
      device_type: d.type,
      contractor_id: null,
      unit_code: unitCode,
      qty: 1,
      gps_lat: lat,
      gps_lng: lng,
      size: d.type === 'handhole' ? hhSize : (d.subtype || null),
      splice_type: (d.type === 'splitter_1x4' ? '1x4' : d.type === 'splitter_2x8' ? '1x8' : d.spliceType) || null,
      handhole_id: d.handholeId || null,
      fiber_count: d.fiberCount || null,
      tray_count: null,
      notes: d.notes || null,
    };
  });

  try {
    const result = await apiPost('/publish-tracer', {
      project_id: projectId,
      segments,
      devices,
    });

    if (result.success) {
      console.log(`[publishTracerData] Done: ${JSON.stringify(result.counts)}`);
      return {
        success: true,
        counts: result.counts,
        timestamp: result.timestamp,
      };
    } else {
      errors.push(result.error || 'Unknown error');
      return { success: false, errors };
    }
  } catch (err) {
    errors.push(err.message);
    return { success: false, errors };
  }
}

// ===== LOAD TRACER FROM DB =====

/**
 * Load existing project data from Postgres back into Map Tracer format.
 */
export async function loadTracerFromSheets(projectId) {
  console.log(`[loadTracerFromSheets] Loading project: ${projectId}`);

  _dbOnline = null;
  _dbCheckedAt = 0;

  const online = await checkDbConnection();
  if (!online) {
    console.error('[loadTracerFromSheets] Database offline — cannot sync');
    return { success: false, error: 'Database offline' };
  }

  try {
    const [segRows, devRows, projects] = await Promise.all([
      loadSegments(projectId),
      loadDevices(projectId),
      loadProjects(projectId),
    ]);
    console.log(`[loadTracerFromSheets] Loaded: ${segRows.length} segs, ${devRows.length} devices`);

    // Convert segments to tracer format
    const segments = segRows.map(seg => ({
      id: seg.segment_id,
      lineType: seg.cable_type ? 'fiber' : 'conduit',
      points: [
        { lat: parseFloat(seg.gps_start_lat), lng: parseFloat(seg.gps_start_lng) },
        { lat: parseFloat(seg.gps_end_lat), lng: parseFloat(seg.gps_end_lng) },
      ].filter(p => !isNaN(p.lat) && !isNaN(p.lng)),
      footage: parseFloat(seg.footage) || 0,
      street: seg.street || '',
      conduitSpec: seg.duct_count ? `${seg.duct_count}x1.25` : (seg.bore_code === 'UG1' ? '1x1.25' : seg.bore_code === 'UG3' ? '3x1.25' : '2x1.25'),
      fiberSpec: seg.cable_type || '',
      startSnap: seg.from_structure ? { deviceId: seg.from_structure } : null,
      endSnap: seg.to_structure ? { deviceId: seg.to_structure } : null,
      cableType: seg.cable_type || '',
    }));

    // Normalize legacy device_type values
    devRows.forEach(d => { if (d.device_type === 'terminal_1x4') d.device_type = 'splitter_1x4'; });

    // Convert devices to tracer format
    const devices = devRows
      .filter(d => {
        if (d.device_type === 'ground_rod') return true;
        const lat = parseFloat(d.gps_lat);
        const lng = parseFloat(d.gps_lng);
        return !isNaN(lat) && !isNaN(lng);
      })
      .map(d => {
        const lat = parseFloat(d.gps_lat) || 0;
        const lng = parseFloat(d.gps_lng) || 0;
        const base = {
          id: d.device_id,
          type: d.device_type,
          position: { lat, lng },
          unitCode: d.unit_code || '',
          notes: d.notes || '',
        };
        if (d.device_type === 'handhole') {
          base.subType = d.size || '';
        } else if (d.device_type === 'splice_point' || d.device_type === 'splitter_1x4' || d.device_type === 'splitter_2x8') {
          base.spliceType = d.splice_type || 'ring_cut';
          base.fiberCount = parseInt(d.fiber_count) || 0;
          base.handholeId = d.handhole_id || '';
        } else if (d.device_type === 'slack_loop') {
          base.subType = d.size || '';
        }
        return base;
      });

    return {
      success: true,
      segments,
      devices,
      project: {
        id: projectId,
        name: projects[0]?.project_name || projects[0]?.name || projectId,
        status: projects[0]?.status || 'Active',
      },
    };
  } catch (err) {
    console.error('[loadTracerFromSheets] Error:', err);
    return { success: false, error: err.message };
  }
}

// ===== PROJECT LIST =====

export async function loadProjectsList() {
  try {
    const projects = await apiGet('/projects');
    return projects.map(p => ({
      id: p.project_id,
      client: p.client || '',
      name: p.name || '',
      workOrder: p.wo_number || '',
      status: p.status || 'Active',
    }));
  } catch (err) {
    console.error('[loadProjectsList] Error:', err);
    return [];
  }
}

// ===== DASHBOARD STATS (aggregated across all projects) =====

export async function loadDashboardStats() {
  try {
    const resp = await fetch(`${API_BASE}/dashboard-stats`);
    const json = await resp.json();
    if (json.success) return json.data;
    console.error('[loadDashboardStats]', json.error);
    return null;
  } catch (err) {
    console.error('[loadDashboardStats]', err);
    return null;
  }
}

// ===== IMPORT / BULK OPERATIONS (Postgres-backed) =====

/**
 * Import a full project from AI extraction (legacy format).
 * Converts to segments+devices and publishes via /api/publish-tracer.
 */
export async function importProject(extractionData, projectId) {
  const online = await checkDbConnection();
  if (!online) return { success: false, error: 'Database offline' };

  const handholes = extractionData.handholes || [];
  const flowerpots = extractionData.flowerpots || [];
  const groundRods = extractionData.ground_rods || [];
  const segments = extractionData.segments || [];
  const splicePoints = extractionData.splice_points || [];
  const projectData = extractionData.project || extractionData;

  // Build devices array
  const devices = [];
  handholes.forEach((hh, i) => {
    devices.push({
      device_id: hh.id || `${projectId}-HH-${i + 1}`,
      device_type: 'handhole',
      unit_code: hh.code || 'UG20',
      qty: hh.qty || 1,
      gps_lat: hh.gps_lat || null,
      gps_lng: hh.gps_lng || null,
      size: hh.type || '',
      notes: hh.section || null,
    });
  });
  flowerpots.forEach((fp, i) => {
    devices.push({
      device_id: fp.id || `${projectId}-FP-${i + 1}`,
      device_type: 'flowerpot',
      unit_code: fp.code || 'UG12',
      qty: fp.qty || 1,
      gps_lat: fp.gps_lat || null,
      gps_lng: fp.gps_lng || null,
    });
  });
  groundRods.forEach((gr, i) => {
    devices.push({
      device_id: gr.id || `${projectId}-GR-${i + 1}`,
      device_type: 'ground_rod',
      unit_code: gr.code || 'UG13',
      qty: gr.qty || 1,
      handhole_id: gr.handhole_id || null,
    });
  });
  splicePoints.forEach((sp, i) => {
    const spliceType = sp.splice_type || '1x4';
    const deviceType = spliceType === '1x4' ? 'splitter_1x4' : (spliceType === '1x8' || spliceType === '2x8') ? 'splitter_2x8' : 'splice_point';
    devices.push({
      device_id: sp.id || `${projectId}-SP-${i + 1}`,
      device_type: deviceType,
      gps_lat: sp.gps_lat || null,
      gps_lng: sp.gps_lng || null,
      splice_type: spliceType,
      handhole_id: sp.location || sp.handhole_id || null,
      fiber_count: sp.fiber_count || 2,
      tray_count: sp.tray_count || 1,
    });
  });

  // Build segments array
  const dbSegments = segments.map((seg, i) => {
    const boreCode = seg.bore?.code || '';
    return {
      segment_id: seg.id || `${projectId}-SEG-${i + 1}`,
      section: seg.section || null,
      from_structure: seg.from_structure_id || seg.from_handhole || null,
      to_structure: seg.to_structure_id || seg.to_handhole || null,
      footage: seg.footage || 0,
      street: seg.street || null,
      gps_start_lat: seg.gps_start_lat || null,
      gps_start_lng: seg.gps_start_lng || null,
      gps_end_lat: seg.gps_end_lat || null,
      gps_end_lng: seg.gps_end_lng || null,
      bore_code: boreCode || null,
      bore_footage: seg.bore?.qty || seg.footage || 0,
      duct_count: seg.bore?.duct_count || 1,
      pull_code: seg.pull?.code || null,
      pull_footage: seg.pull?.qty || seg.footage || 0,
      cable_type: seg.pull?.cable_type || null,
    };
  });

  // Create project first
  await apiPost('/projects', {
    project_id: projectId,
    client: projectData.customer || '',
    name: projectData.project_name || projectId,
    wo_number: projectData.po_number || null,
    total_value: projectData.total_value || 0,
    status: 'Active',
    rate_card: projectData.rate_card_id || 'vexus-la-tx-2026',
    created_by: projectData.created_by || 'matt@lytcomm.com',
  });

  // Publish segments + devices
  const result = await apiPost('/publish-tracer', {
    project_id: projectId,
    segments: dbSegments,
    devices,
  });

  return {
    success: result.success,
    counts: result.counts || {},
    errors: result.error ? [result.error] : undefined,
  };
}

/**
 * Import a project from the new EXTRACTION_PROMPT.md JSON format.
 * Handles unified structures[], line_items[], nested gps { lat, lng }.
 */
export async function importProjectFromExtraction(extractionData, projectId) {
  const online = await checkDbConnection();
  if (!online) return { success: false, error: 'Database offline' };

  const proj = extractionData.project || {};
  const segments = extractionData.segments || [];
  const structures = extractionData.structures || [];
  const splicePoints = extractionData.splice_points || [];
  const lineItems = extractionData.line_items || [];

  console.log(`[importProjectFromExtraction] ${segments.length} SEG, ${structures.length} STRUCT, ${splicePoints.length} SP, ${lineItems.length} LI`);

  // Create project
  await apiPost('/projects', {
    project_id: projectId,
    client: proj.client || 'Vexus',
    name: proj.name || '',
    wo_number: proj.work_order_number || null,
    status: 'Active',
    rate_card: proj.rate_card || 'vexus-la-tx-2026',
    created_by: 'matt@lytcomm.com',
  });

  // Build DB segments
  const dbSegments = segments.map((seg, i) => {
    const boreCode = seg.duct_count <= 3 ? `UG${seg.duct_count}` : 'UG16';
    return {
      segment_id: seg.segment_id || `${projectId}-SEG-${i + 1}`,
      from_structure: seg.from_structure || null,
      to_structure: seg.to_structure || null,
      footage: seg.footage || 0,
      street: seg.street_name || seg.description || null,
      gps_start_lat: seg.gps_start?.lat || null,
      gps_start_lng: seg.gps_start?.lng || null,
      gps_end_lat: seg.gps_end?.lat || null,
      gps_end_lng: seg.gps_end?.lng || null,
      bore_code: boreCode,
      bore_footage: seg.footage || 0,
      duct_count: seg.duct_count || 1,
      pull_code: 'UG4',
      pull_footage: seg.footage || 0,
      cable_type: seg.cable_type || null,
    };
  });

  // Build DB devices from structures + splice points
  const dbDevices = [];
  structures.forEach((struct, i) => {
    const t = struct.type || 'handhole';
    dbDevices.push({
      device_id: struct.id || `${projectId}-${t.toUpperCase().replace('_', '')}-${i + 1}`,
      device_type: t,
      unit_code: struct.unit_code || (t === 'handhole' ? 'UG17' : t === 'flowerpot' ? 'UG12' : t === 'ground_rod' ? 'UG13' : 'UG20'),
      qty: 1,
      gps_lat: struct.gps?.lat || null,
      gps_lng: struct.gps?.lng || null,
      size: struct.size || null,
    });
  });
  splicePoints.forEach((sp, i) => {
    const spliceType = sp.splice_type || 'ring_cut';
    const deviceType = spliceType === '1x4' ? 'splitter_1x4' : (spliceType === '1x8' || spliceType === '2x8') ? 'splitter_2x8' : 'splice_point';
    dbDevices.push({
      device_id: sp.splice_id || `${projectId}-SP-${i + 1}`,
      device_type: deviceType,
      gps_lat: sp.gps?.lat || null,
      gps_lng: sp.gps?.lng || null,
      splice_type: spliceType,
      handhole_id: sp.handhole_id || null,
      fiber_count: sp.fiber_count || 0,
      tray_count: 1,
    });
  });

  // Publish segments + devices
  const result = await apiPost('/publish-tracer', {
    project_id: projectId,
    segments: dbSegments,
    devices: dbDevices,
  });

  const counts = result.counts || {};

  // Import line items if present (admin path — client rates fetched server-side)
  if (lineItems.length > 0) {
    const full = await fetchFullRates('vexus-la-tx-2026');
    const fullRates = full?.rates || {};
    const clientField = full?.priceField || 'vexus';
    const liPayload = lineItems.map((item, i) => {
      const code = item.code || '';
      const rateInfo = fullRates[code] || {};
      const vexusRate = rateInfo[clientField] || 0;
      const contractorRate = rateInfo.contractor_rate || 0;
      return {
        line_item_id: `${projectId}-LI-${String(i + 1).padStart(3, '0')}`,
        project_id: projectId,
        code,
        description: item.description || rateInfo.description || '',
        uom: item.uom || rateInfo.uom || '',
        quantity: item.quantity || 0,
        segment_id: item.segment_id || null,
        structure_id: item.structure_id || null,
        splice_id: item.splice_id || null,
        vexus_rate: vexusRate,
        contractor_rate: contractorRate,
        margin: vexusRate - contractorRate,
        status: 'Planned',
      };
    });
    const liResult = await apiPost('/line-items', { line_items: liPayload });
    counts.lineItems = liResult.count || 0;
  }

  return {
    success: result.success,
    counts,
    errors: result.error ? [result.error] : undefined,
  };
}

/**
 * Bulk assign segments to a contractor for a given work type.
 */
export async function bulkAssignSegments(segmentIds, workType, contractorName, projectId, contractorEmail) {
  const online = await checkDbConnection();
  if (!online) return { success: false, error: 'Database offline' };

  const fieldMap = {
    boring: 'boring_assigned_to',
    pulling: 'pulling_assigned_to',
    splicing: 'splicing_assigned_to',
  };
  const field = fieldMap[workType];
  if (!field) return { success: false, error: `Invalid work type: ${workType}` };

  let updated = 0;
  const errors = [];

  for (const segId of segmentIds) {
    try {
      const ok = await updateSegmentField(segId, field, contractorName);
      if (ok) updated++;
      else errors.push(`Failed: ${segId}`);
    } catch (err) {
      errors.push(`${segId}: ${err.message}`);
    }
  }

  // Create assignment record in Postgres
  try {
    const assignmentId = `ASGN-${Date.now()}`;
    await apiPost('/assignments', {
      assignment_id: assignmentId,
      project_id: projectId || '',
      segment_id: segmentIds[0] || null,
      segment_ids: JSON.stringify(segmentIds),
      contractor_email: contractorName,
      contractor_name: contractorName,
      assigned_by: '',
      phase: workType,
      work_type: workType,
      status: 'Pending',
    });
  } catch (err) {
    console.warn('[mapService] Assignment record failed:', err);
  }

  // Email contractor if email provided (C1: WORKFLOW Step 60)
  if (contractorEmail && updated > 0) {
    try {
      const { notifyNewAssignment } = await import('./notificationService');
      await notifyNewAssignment(contractorEmail, projectId || 'Unknown Project', segmentIds, workType);
    } catch (err) {
      console.warn('[mapService] Email notification failed:', err);
    }
  }

  return {
    success: errors.length === 0,
    updated,
    total: segmentIds.length,
    errors: errors.length > 0 ? errors : undefined,
  };
}

// ===== LINE ITEMS (Postgres-backed) =====

export async function getProjectLineItems(projectId) {
  return loadLineItems(projectId);
}

export async function importLineItems(projectId, lineItems, rateCardId) {
  const online = await checkDbConnection();
  if (!online) return { success: false, error: 'Database offline' };

  const full = await fetchFullRates(rateCardId || 'vexus-la-tx-2026');
  const fullRates = full?.rates || {};
  const clientField = full?.priceField || 'vexus';
  const liPayload = lineItems.map((item, i) => {
    const code = item.code || '';
    const rateInfo = fullRates[code] || {};
    const vexusRate = rateInfo[clientField] || 0;
    const contractorRate = rateInfo.contractor_rate || 0;
    return {
      line_item_id: `${projectId}-LI-${String(i + 1).padStart(3, '0')}`,
      project_id: projectId,
      code,
      description: item.description || rateInfo.description || '',
      uom: item.uom || rateInfo.uom || '',
      quantity: item.quantity || 0,
      segment_id: item.segment_id || null,
      structure_id: item.structure_id || null,
      splice_id: item.splice_id || null,
      vexus_rate: vexusRate,
      contractor_rate: contractorRate,
      margin: vexusRate - contractorRate,
      status: 'Not Started',
    };
  });

  const result = await apiPost('/line-items', { line_items: liPayload });
  return {
    success: result.success,
    imported: result.count || 0,
    total: lineItems.length,
    errors: result.error ? [result.error] : undefined,
  };
}

export async function updateLineItemRate(projectId, lineItemId, contractorRate) {
  const online = await checkDbConnection();
  if (!online) return false;
  try {
    // Get current line item to calculate new margin
    const items = await apiGet(`/line-items?project_id=${encodeURIComponent(projectId)}`);
    const item = items.find(li => li.line_item_id === lineItemId);
    if (!item) return false;

    const vexusRate = parseFloat(item.vexus_rate) || 0;
    const margin = vexusRate - contractorRate;

    const r1 = await apiPatch('/line-items', { line_item_id: lineItemId, field: 'contractor_rate', value: contractorRate });
    const r2 = await apiPatch('/line-items', { line_item_id: lineItemId, field: 'margin', value: margin });
    return r1.success && r2.success;
  } catch (err) {
    console.error('[mapService] updateLineItemRate failed:', err);
    return false;
  }
}

// ===== RATE CARDS (still Google Sheets) =====

export async function loadRateCards(rateCardId) {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    const rows = await readSheet(DB.RATE_CARDS, 'A1:H1000');
    const parsed = rowsToObjects(rows);
    return rateCardId ? parsed.filter(r => r.rate_card_id === rateCardId) : parsed;
  } catch (err) {
    console.error('[mapService] loadRateCards failed:', err);
    return [];
  }
}

// ===== WORK LOG (still Google Sheets) =====

export async function logAction(projectId, segmentId, userEmail, action, details, gpsCoords) {
  const online = await checkDbConnection();
  if (!online) {
    console.log(`[mapService] Demo log: ${action} on ${segmentId}`);
    return true;
  }
  const logId = `LOG-${Date.now()}`;
  return await appendRow(DB.WORK_LOG, 'A:H', [[
    logId,
    new Date().toISOString(),
    projectId || '',
    segmentId || '',
    userEmail || '',
    action || '',
    typeof details === 'object' ? JSON.stringify(details) : (details || ''),
    gpsCoords || '',
  ]]);
}

// ===== ISSUES (still Google Sheets) =====

export async function loadIssues(projectId) {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    const rows = await readSheet(DB.ISSUES, 'A1:K1000');
    const parsed = rowsToObjects(rows);
    return projectId ? parsed.filter(r => r.project_id === projectId) : parsed;
  } catch (err) {
    console.error('[mapService] loadIssues failed:', err);
    return [];
  }
}

export async function createIssue(projectId, segmentId, reportedBy, issueType, description) {
  const online = await checkDbConnection();
  if (!online) return false;
  const issueId = `ISS-${Date.now()}`;
  return await appendRow(DB.ISSUES, 'A:K', [[
    issueId, projectId || '', segmentId || '', reportedBy || '',
    new Date().toISOString(), issueType || 'Other', description || '',
    '', '', '', 'Open',
  ]]);
}

export async function resolveIssue(issueId, resolvedBy, resolution) {
  const online = await checkDbConnection();
  if (!online) return false;
  try {
    const rows = await readSheet(DB.ISSUES, 'A1:K1000');
    if (!rows || rows.length < 2) return false;
    const headers = rows[0];
    const idCol = headers.indexOf('issue_id');
    const rowIndex = rows.slice(1).findIndex(r => r[idCol] === issueId);
    if (rowIndex === -1) return false;
    const actualRow = rowIndex + 2;
    await writeSheet(DB.ISSUES, `H${actualRow}:K${actualRow}`, [[resolution, resolvedBy, new Date().toISOString(), 'Resolved']]);
    return true;
  } catch (err) {
    console.error('[mapService] resolveIssue failed:', err);
    return false;
  }
}

// ===== LOCATES (still Google Sheets) =====

export async function loadLocates(projectId) {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    const rows = await readSheet(MAP_SS, 'Locates!A1:M5000');
    if (!rows || rows.length < 2) return [];
    const headers = rows[0];
    return rows.slice(1)
      .filter(r => r.length > 1 && r[0])
      .map(r => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = r[i] || ''; });
        return obj;
      })
      .filter(loc => !projectId || loc.project_id === projectId);
  } catch (err) {
    console.error('[mapService] loadLocates failed:', err);
    return [];
  }
}

export async function createLocate(locateData) {
  const online = await checkDbConnection();
  if (!online) return false;
  const locateId = `LOC-${Date.now()}`;
  const ts = new Date().toISOString();
  return await appendRow(MAP_SS, 'Locates!A:M', [[
    locateId,
    locateData.project_id || '',
    locateData.segment_id || '',
    locateData.ticket_number || '',
    locateData.call_date || '',
    locateData.expiration_date || '',
    locateData.status || 'Requested',
    locateData.utility_type || '',
    locateData.notes || '',
    '', '', '', ts,
  ]]);
}

export async function updateLocateField(locateId, field, value) {
  const online = await checkDbConnection();
  if (!online) return false;
  try {
    const rows = await readSheet(MAP_SS, 'Locates!A1:M5000');
    if (!rows || rows.length < 2) return false;
    const headers = rows[0];
    const idCol = headers.indexOf('locate_id');
    const fieldCol = headers.indexOf(field);
    if (idCol === -1 || fieldCol === -1) return false;
    const rowIndex = rows.slice(1).findIndex(r => r[idCol] === locateId);
    if (rowIndex === -1) return false;
    const actualRow = rowIndex + 2;
    const colLetter = String.fromCharCode(65 + fieldCol);
    await writeSheet(MAP_SS, `Locates!${colLetter}${actualRow}`, [[value]]);
    return true;
  } catch (err) {
    console.error('[mapService] updateLocateField failed:', err);
    return false;
  }
}

export async function verifyLocate(locateId, verifiedBy) {
  const online = await checkDbConnection();
  if (!online) return false;
  try {
    const rows = await readSheet(MAP_SS, 'Locates!A1:M5000');
    if (!rows || rows.length < 2) return false;
    const headers = rows[0];
    const idCol = headers.indexOf('locate_id');
    const statusCol = headers.indexOf('status');
    const verByCol = headers.indexOf('verified_by');
    const verDateCol = headers.indexOf('verified_date');
    if (idCol === -1) return false;
    const rowIndex = rows.slice(1).findIndex(r => r[idCol] === locateId);
    if (rowIndex === -1) return false;
    const actualRow = rowIndex + 2;
    const statusLetter = String.fromCharCode(65 + statusCol);
    const verByLetter = String.fromCharCode(65 + verByCol);
    const verDateLetter = String.fromCharCode(65 + verDateCol);
    await writeSheet(MAP_SS, `Locates!${statusLetter}${actualRow}:${verDateLetter}${actualRow}`, [[
      'Verified', verifiedBy || '', new Date().toISOString(),
    ]]);
    return true;
  } catch (err) {
    console.error('[mapService] verifyLocate failed:', err);
    return false;
  }
}

export function getLocatesForSegment(locates, segmentId) {
  return locates.filter(loc => loc.segment_id === segmentId);
}

export function areLocatesVerified(locates, segmentId) {
  const segLocates = getLocatesForSegment(locates, segmentId);
  if (segLocates.length === 0) return false;
  const now = new Date();
  return segLocates.every(loc => {
    if (loc.status !== 'Verified') return false;
    if (loc.expiration_date) {
      const expiry = new Date(loc.expiration_date);
      if (expiry < now) return false;
    }
    return true;
  });
}

// ===== DAMAGE REPORTS (still Google Sheets) =====

const DAMAGE_HEADERS = ['report_id','project_id','segment_id','reported_by','reported_by_email','timestamp','gps_lat','gps_lng','utility_type','severity','description','photos','resolution','status'];

export async function loadDamageReports(projectId) {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    const rows = await readSheet(DB.ASSIGNMENTS, 'DamageReports!A1:N5000');
    if (!rows || rows.length <= 1) return [];
    const headers = rows[0];
    const reports = [];
    for (let i = 1; i < rows.length; i++) {
      const obj = {};
      headers.forEach((h, j) => { obj[h] = rows[i][j] || ''; });
      if (!projectId || obj.project_id === projectId) reports.push(obj);
    }
    return reports;
  } catch (err) {
    console.error('[mapService] loadDamageReports failed:', err);
    return [];
  }
}

export async function createDamageReport(reportData) {
  const online = await checkDbConnection();
  if (!online) return false;
  try {
    const reportId = `DMG-${Date.now()}`;
    const row = DAMAGE_HEADERS.map(h => {
      if (h === 'report_id') return reportId;
      if (h === 'timestamp') return new Date().toISOString();
      if (h === 'status') return 'Open';
      return reportData[h] || '';
    });
    await appendRow(DB.ASSIGNMENTS, 'DamageReports!A:N', [row]);
    return reportId;
  } catch (err) {
    console.error('[mapService] createDamageReport failed:', err);
    return false;
  }
}

// ===== INVOICES (still Google Sheets) =====

const INVOICE_HEADERS = [
  'invoice_id','project_id','contractor_company','contractor_email','invoice_number',
  'status','line_items_json','subtotal','retainage_pct','retainage_amount','net_amount',
  'created_at','submitted_at','approved_by','approved_at','rejected_reason',
  'paid_date','paid_amount','check_number','retainage_status','retainage_released_date','notes'
];

export async function loadInvoices(projectId) {
  const online = await checkDbConnection();
  if (!online) return [];
  try {
    const rows = await readSheet(MAP_SS, 'Invoices!A1:V5000');
    if (!rows || rows.length <= 1) return [];
    const headers = rows[0];
    const invoices = [];
    for (let i = 1; i < rows.length; i++) {
      const obj = {};
      headers.forEach((h, j) => { obj[h] = rows[i][j] || ''; });
      if (!projectId || obj.project_id === projectId) invoices.push(obj);
    }
    return invoices;
  } catch (err) {
    console.error('[mapService] loadInvoices failed:', err);
    return [];
  }
}

export async function loadContractorInvoices(contractorEmail) {
  const all = await loadInvoices(null);
  return all.filter(inv => inv.contractor_email === contractorEmail);
}

export async function createInvoice(invoiceData) {
  const online = await checkDbConnection();
  if (!online) return false;
  try {
    const invoiceId = `INV-${Date.now()}`;
    const subtotal = parseFloat(invoiceData.subtotal) || 0;
    const retainagePct = 10;
    const retainageAmount = Math.round(subtotal * retainagePct / 100 * 100) / 100;
    const netAmount = Math.round((subtotal - retainageAmount) * 100) / 100;

    const row = INVOICE_HEADERS.map(h => {
      switch (h) {
        case 'invoice_id': return invoiceId;
        case 'retainage_pct': return String(retainagePct);
        case 'retainage_amount': return String(retainageAmount);
        case 'net_amount': return String(netAmount);
        case 'status': return 'Draft';
        case 'created_at': return new Date().toISOString();
        case 'retainage_status': return 'Held';
        default: return invoiceData[h] || '';
      }
    });
    await appendRow(MAP_SS, 'Invoices!A:V', [row]);
    return invoiceId;
  } catch (err) {
    console.error('[mapService] createInvoice failed:', err);
    return false;
  }
}

export async function updateInvoiceField(invoiceId, field, value) {
  const online = await checkDbConnection();
  if (!online) return false;
  try {
    const rows = await readSheet(MAP_SS, 'Invoices!A1:V5000');
    if (!rows || rows.length === 0) return false;
    const headers = rows[0];
    const idCol = headers.indexOf('invoice_id');
    const fieldCol = headers.indexOf(field);
    if (idCol === -1 || fieldCol === -1) return false;

    for (let i = 1; i < rows.length; i++) {
      if (rows[i][idCol] === invoiceId) {
        const colLetter = String.fromCharCode(65 + fieldCol);
        const rowNum = i + 1;
        await writeSheet(MAP_SS, `Invoices!${colLetter}${rowNum}`, [[value]]);
        return true;
      }
    }
    return false;
  } catch (err) {
    console.error('[mapService] updateInvoiceField failed:', err);
    return false;
  }
}

export async function submitInvoice(invoiceId) {
  const now = new Date().toISOString();
  await updateInvoiceField(invoiceId, 'status', 'Submitted');
  await updateInvoiceField(invoiceId, 'submitted_at', now);
  return true;
}

export async function approveInvoice(invoiceId, approvedBy) {
  const now = new Date().toISOString();
  await updateInvoiceField(invoiceId, 'status', 'Approved');
  await updateInvoiceField(invoiceId, 'approved_by', approvedBy);
  await updateInvoiceField(invoiceId, 'approved_at', now);
  return true;
}

export async function markInvoicePaid(invoiceId, paidAmount, checkNumber) {
  const now = new Date().toISOString();
  await updateInvoiceField(invoiceId, 'status', 'Paid');
  await updateInvoiceField(invoiceId, 'paid_date', now);
  await updateInvoiceField(invoiceId, 'paid_amount', String(paidAmount));
  await updateInvoiceField(invoiceId, 'check_number', checkNumber || '');
  return true;
}

export async function releaseRetainage(invoiceId) {
  const now = new Date().toISOString();
  await updateInvoiceField(invoiceId, 'retainage_status', 'Released');
  await updateInvoiceField(invoiceId, 'retainage_released_date', now);
  return true;
}

// ===== CHANGE ORDERS (still Google Sheets) =====

const CHANGE_ORDER_HEADERS = ['co_id','project_id','description','reason','status','line_items_json','original_value','revised_value','delta','created_by','created_date','approved_by','approved_date'];

export async function loadChangeOrders(projectId) {
  try {
    const data = await readSheet(MAP_SS, 'ChangeOrders!A1:M1000');
    if (!data || data.length < 2) return [];
    const headers = data[0];
    const rows = data.slice(1).map(row => {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = row[i] || ''; });
      return obj;
    });
    return projectId ? rows.filter(co => co.project_id === projectId) : rows;
  } catch (err) {
    console.error('[mapService] loadChangeOrders failed:', err);
    return [];
  }
}

export async function createChangeOrder(coData) {
  try {
    const coId = `CO-${Date.now().toString(36).toUpperCase()}`;
    const row = CHANGE_ORDER_HEADERS.map(h => {
      if (h === 'co_id') return coId;
      if (h === 'created_date') return new Date().toISOString();
      return coData[h] || '';
    });
    await appendRow(MAP_SS, 'ChangeOrders!A:M', [row]);
    return { success: true, co_id: coId };
  } catch (err) {
    console.error('[mapService] createChangeOrder failed:', err);
    return { success: false, error: err.message };
  }
}

export async function updateChangeOrderField(coId, field, value) {
  try {
    const data = await readSheet(MAP_SS, 'ChangeOrders!A1:M1000');
    if (!data || data.length < 2) return false;
    const headers = data[0];
    const colIdx = headers.indexOf(field);
    if (colIdx === -1) return false;
    for (let i = 1; i < data.length; i++) {
      if (data[i][0] === coId) {
        const colLetter = String.fromCharCode(65 + colIdx);
        const range = `ChangeOrders!${colLetter}${i + 1}`;
        await writeSheet(MAP_SS, range, [[value]]);
        return true;
      }
    }
    return false;
  } catch (err) {
    console.error('[mapService] updateChangeOrderField failed:', err);
    return false;
  }
}

// ===== CONTRACTOR EMAIL LOOKUP =====

/**
 * Look up a contractor's email by company name from the Users sheet.
 * Returns email string or null if not found.
 */
export async function lookupContractorEmail(companyName) {
  if (!companyName) return null;
  try {
    const rows = await readSheet(DB.USERS, 'A1:J500');
    if (!rows || rows.length < 2) return null;
    const headers = rows[0];
    const emailIdx = headers.findIndex(h => (h || '').toString().toLowerCase() === 'email');
    const companyIdx = headers.findIndex(h => (h || '').toString().toLowerCase() === 'company');
    if (emailIdx === -1 || companyIdx === -1) return null;
    const target = companyName.toLowerCase().trim();
    for (let i = 1; i < rows.length; i++) {
      if ((rows[i][companyIdx] || '').toString().toLowerCase().trim() === target) {
        return rows[i][emailIdx] || null;
      }
    }
    return null;
  } catch (err) {
    console.warn('[mapService] lookupContractorEmail failed:', err);
    return null;
  }
}

// ===== PHASE DETECTION (Contractor Portal Filtering) =====

/** Which device types each phase needs to see */
export const PHASE_DEVICE_TYPES = {
  boring: ['handhole', 'flowerpot', 'pedestal'],
  pulling: ['handhole'],
  splicing: ['splice_point', 'splitter_1x4', 'splitter_2x8', 'cabinet', 'fat', 'handhole'],
};

/** Which status field each phase reads/writes */
export const PHASE_STATUS_FIELD = {
  boring: 'boring_status',
  pulling: 'pulling_status',
  splicing: 'splicing_status',
};

/**
 * Detect which phases a contractor is assigned to, based on segment *_assigned_to fields.
 * Returns array like ['boring'] or ['boring', 'pulling'].
 */
export function detectContractorPhases(segments, companyName) {
  if (!companyName || !segments || segments.length === 0) return ['boring'];
  const lc = companyName.toLowerCase();
  const phases = new Set();
  for (const s of segments) {
    if ((s.boring_assigned_to || '').toLowerCase().includes(lc)) phases.add('boring');
    if ((s.pulling_assigned_to || '').toLowerCase().includes(lc)) phases.add('pulling');
    if ((s.splicing_assigned_to || '').toLowerCase().includes(lc)) phases.add('splicing');
  }
  return phases.size > 0 ? [...phases] : ['boring'];
}

/** Merged set of allowed device types for one or more phases */
export function getPhaseDeviceTypes(phases) {
  const types = new Set();
  for (const phase of phases) {
    const allowed = PHASE_DEVICE_TYPES[phase];
    if (allowed) allowed.forEach(t => types.add(t));
  }
  return types;
}

/** Status field string for the primary (first) phase */
export function getPhaseStatusField(phases) {
  return PHASE_STATUS_FIELD[phases[0]] || 'boring_status';
}

/** Filter devices to only those allowed by the contractor's phases */
export function filterDevicesByPhase(devices, phases) {
  const allowedTypes = getPhaseDeviceTypes(phases);
  return devices.filter(d => allowedTypes.has(d.device_type));
}

/**
 * Filter segments to only those where the contractor is assigned for their detected phase(s).
 * Splicing phase shows no segments (splicers work at device locations).
 */
export function filterSegmentsByPhase(segments, phases, companyName) {
  if (!companyName) return segments;
  const lc = companyName.toLowerCase();
  return segments.filter(s => {
    if (phases.includes('boring') && (s.boring_assigned_to || '').toLowerCase().includes(lc)) return true;
    if (phases.includes('pulling') && (s.pulling_assigned_to || '').toLowerCase().includes(lc)) return true;
    // Splicing phase does NOT show segments — splicers work at devices
    return false;
  });
}

// ===== UTILITY FUNCTIONS =====

export function getStatusColor(status) {
  const map = {
    'Not Started': STATUS_COLORS.NOT_STARTED,
    'Potholing': STATUS_COLORS.IN_PROGRESS,
    'Pothole Approved': STATUS_COLORS.CRITICAL_PATH,
    'In Progress': STATUS_COLORS.IN_PROGRESS,
    'Complete': STATUS_COLORS.COMPLETE,
    'QC Approved': STATUS_COLORS.QC_APPROVED,
    'Blocked': STATUS_COLORS.BLOCKED,
    'Issue': STATUS_COLORS.ISSUE,
  };
  return map[status] || STATUS_COLORS.NOT_STARTED;
}

export function groupBySection(segments) {
  return segments.reduce((groups, seg) => {
    const section = seg.section || 'Unknown';
    if (!groups[section]) groups[section] = [];
    groups[section].push(seg);
    return groups;
  }, {});
}

export function getContractorSegments(segments, company) {
  if (!company) return [];
  const lc = company.toLowerCase();
  return segments.filter(s =>
    (s.boring_assigned_to || '').toLowerCase().includes(lc) ||
    (s.pulling_assigned_to || '').toLowerCase().includes(lc) ||
    (s.splicing_assigned_to || '').toLowerCase().includes(lc)
  );
}

export function sanitizeForContractor(segment) {
  const s = { ...segment };
  delete s.total_value;
  delete s.work_items;
  delete s.boring_actual_cost;
  delete s.pulling_actual_cost;
  return s;
}

export function getProjectStats(segments) {
  const stats = {
    totalSegments: segments.length, totalFootage: 0,
    boringComplete: 0, boringInProgress: 0,
    pullingComplete: 0, pullingInProgress: 0,
    splicingComplete: 0, splicingInProgress: 0,
    issues: 0,
  };
  segments.forEach(seg => {
    stats.totalFootage += parseFloat(seg.footage) || 0;
    if (seg.boring_status === 'QC Approved' || seg.boring_status === 'Complete') stats.boringComplete++;
    else if (seg.boring_status === 'In Progress') stats.boringInProgress++;
    if (seg.pulling_status === 'QC Approved' || seg.pulling_status === 'Complete') stats.pullingComplete++;
    else if (seg.pulling_status === 'In Progress') stats.pullingInProgress++;
    if (seg.splicing_status === 'QC Approved' || seg.splicing_status === 'Complete') stats.splicingComplete++;
    else if (seg.splicing_status === 'In Progress') stats.splicingInProgress++;
    if (seg.boring_status === 'Issue' || seg.pulling_status === 'Issue') stats.issues++;
  });
  return stats;
}

// ===== PRODUCTION SUBMISSIONS (Track 1: Daily Production to Client) =====

export async function loadProductionSubmissions(projectId) {
  return await apiGet(`/production-submissions?project_id=${encodeURIComponent(projectId)}`);
}

export async function loadAllProductionSubmissions() {
  return await apiGet('/production-submissions?project_id=ALL');
}

export async function getProductionSubmission(submissionId) {
  const resp = await apiGet(`/production-submissions?submission_id=${encodeURIComponent(submissionId)}`);
  return Array.isArray(resp) ? resp[0] || null : resp;
}

export async function createProductionSubmission(data) {
  const submissionId = `SUB-${Date.now()}`;
  const result = await apiPost('/production-submissions', {
    submission_id: submissionId,
    ...data,
  });
  return result.success ? submissionId : null;
}

export async function updateSubmissionField(submissionId, field, value) {
  const result = await apiPatch('/production-submissions', {
    submission_id: submissionId,
    field,
    value,
  });
  return result.success || false;
}

// ===== PAYMENT INVOICES (Track 2: Traditional Invoices to Client) =====

export async function loadPaymentInvoices(projectId) {
  if (projectId) {
    return await apiGet(`/payment-invoices?project_id=${encodeURIComponent(projectId)}`);
  }
  return await apiGet('/payment-invoices');
}

export async function getPaymentInvoice(invoiceId) {
  const resp = await apiGet(`/payment-invoices?invoice_id=${encodeURIComponent(invoiceId)}`);
  return Array.isArray(resp) ? resp[0] || null : resp;
}

export async function createPaymentInvoice(data) {
  const invoiceId = `PINV-${Date.now()}`;
  const result = await apiPost('/payment-invoices', {
    invoice_id: invoiceId,
    ...data,
  });
  return result.success ? invoiceId : null;
}

export async function updatePaymentInvoiceField(invoiceId, field, value) {
  const result = await apiPatch('/payment-invoices', {
    invoice_id: invoiceId,
    field,
    value,
  });
  return result.success || false;
}

// ===== BILLING STATUS HELPERS =====

/**
 * Update billing status for multiple segments in bulk.
 * phase: 'boring' or 'pulling'
 * status: 'Submitted', 'Approved', or 'Paid'
 */
export async function markSegmentsBilled(segmentIds, phase, status, submissionId) {
  const statusField = phase === 'boring' ? 'boring_billing_status' : 'pulling_billing_status';
  const subField = phase === 'boring' ? 'boring_submission_id' : 'pulling_submission_id';
  const results = [];
  for (const segId of segmentIds) {
    const r1 = await apiPatch('/segments', { segment_id: segId, field: statusField, value: status });
    if (submissionId) {
      await apiPatch('/segments', { segment_id: segId, field: subField, value: submissionId });
    }
    results.push(r1.success);
  }
  return results.every(Boolean);
}

/**
 * Update billing status for multiple devices in bulk.
 * status: 'Submitted', 'Approved', or 'Paid'
 * refId: submission_id or invoice_id
 * refType: 'submission' or 'invoice'
 */
export async function markDevicesBilled(deviceIds, status, refId, refType) {
  const refField = refType === 'invoice' ? 'invoice_id' : 'submission_id';
  const results = [];
  for (const devId of deviceIds) {
    const r1 = await apiPatch('/devices', { device_id: devId, field: 'billing_status', value: status });
    if (refId) {
      await apiPatch('/devices', { device_id: devId, field: refField, value: refId });
    }
    results.push(r1.success);
  }
  return results.every(Boolean);
}

/**
 * Generate the next invoice number for payment invoices.
 * Pattern: LYT-INV-{YYYY}-{NNNN}
 */
export async function getNextInvoiceNumber() {
  const year = new Date().getFullYear();
  const all = await loadPaymentInvoices();
  const yearInvoices = all.filter(inv =>
    inv.invoice_number && inv.invoice_number.includes(`-${year}-`)
  );
  const maxNum = yearInvoices.reduce((max, inv) => {
    const match = inv.invoice_number.match(/-(\d+)$/);
    return match ? Math.max(max, parseInt(match[1], 10)) : max;
  }, 0);
  return `LYT-INV-${year}-${String(maxNum + 1).padStart(4, '0')}`;
}

// ===== MAP NOTES =====

// loadMapNotes supports both legacy (projectId, section, includeResolved) and new options format
export async function loadMapNotes(projectId, section, optionsOrIncludeResolved) {
  const params = [`project_id=${encodeURIComponent(projectId)}`];
  if (section) params.push(`section=${encodeURIComponent(section)}`);

  // Support both boolean (legacy) and options object
  if (typeof optionsOrIncludeResolved === 'object' && optionsOrIncludeResolved !== null) {
    const { includeAll, status, assignedTo, userRole, userCompany, userEmail } = optionsOrIncludeResolved;
    if (includeAll) params.push('include_all=true');
    if (status) params.push(`status=${encodeURIComponent(status)}`);
    if (assignedTo) params.push(`assigned_to=${encodeURIComponent(assignedTo)}`);
    if (userRole) params.push(`user_role=${encodeURIComponent(userRole)}`);
    if (userCompany) params.push(`user_company=${encodeURIComponent(userCompany)}`);
    if (userEmail) params.push(`user_email=${encodeURIComponent(userEmail)}`);
  } else if (optionsOrIncludeResolved === true) {
    params.push('include_all=true');
  }

  return await apiGet(`/map-notes?${params.join('&')}`);
}

export async function createMapNote(noteData) {
  const noteId = `NOTE-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  return await apiPost('/map-notes', { note_id: noteId, ...noteData });
}

export async function updateMapNote(noteId, field, value) {
  return await apiPatch('/map-notes', { note_id: noteId, field, value });
}

export async function editMapNote(noteId, fields, actorEmail, actorName) {
  return await apiPatch('/map-notes', {
    note_id: noteId,
    action: 'edit',
    fields,
    actor_email: actorEmail,
    actor_name: actorName,
  });
}

export async function transitionMapNote(noteId, toStatus, params = {}) {
  return await apiPatch('/map-notes', {
    note_id: noteId,
    action: 'transition',
    status: toStatus,
    ...params,
  });
}

export async function moveMapNote(noteId, lat, lng, actorEmail) {
  return await apiPatch('/map-notes', {
    note_id: noteId,
    action: 'move',
    lat,
    lng,
    actor_email: actorEmail,
  });
}

/**
 * Upload a note photo to Vercel Blob (primary) + Google Drive (backup).
 * @param {string} noteId - Note/pin ID
 * @param {File} file - The image file
 * @param {object} driveCtx - Context for Google Drive folder structure
 *   { projectName, projectId, section, category, authorName, noteId, noteText, status, lat, lng, linkedItem }
 */
export async function uploadNotePhoto(noteId, file, driveCtx = {}) {
  try {
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Primary: upload to Vercel Blob (fast CDN, displays on map)
    const resp = await fetch(`${API_BASE}/note-photos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        note_id: noteId,
        file_name: file.name,
        content_type: file.type || 'image/jpeg',
        base64,
      }),
    });
    const result = await resp.json();

    // Background: also upload to Google Drive (browsable backup)
    if (result.success) {
      import('../services/photoUploadService').then(({ uploadPhoto }) => {
        uploadPhoto(file, { noteId, photoType: 'pin_photo', ...driveCtx }).catch(() => {});
      }).catch(() => {});
    }

    return result;
  } catch (err) {
    console.error('[mapService] uploadNotePhoto failed:', err);
    return { success: false, error: err.message };
  }
}

export async function deleteNotePhoto(noteId, photoUrl) {
  try {
    const resp = await fetch(`${API_BASE}/note-photos`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note_id: noteId, photo_url: photoUrl }),
    });
    return await resp.json();
  } catch (err) {
    console.error('[mapService] deleteNotePhoto failed:', err);
    return { success: false, error: err.message };
  }
}

export async function undoMoveMapNote(noteId, actorEmail) {
  return await apiPatch('/map-notes', {
    note_id: noteId,
    action: 'undo_move',
    actor_email: actorEmail,
  });
}

export async function deleteMapNote(noteId, actorEmail) {
  try {
    const resp = await fetch(`${API_BASE}/map-notes`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note_id: noteId, actor_email: actorEmail }),
    });
    return await resp.json();
  } catch (err) {
    console.error('[mapService] deleteMapNote failed:', err);
    return { success: false, error: err.message };
  }
}

// ===== NOTE REPLIES, READS, ACTIVITY =====

export async function loadNoteReplies(noteId) {
  return apiGet(`/note-replies?note_id=${encodeURIComponent(noteId)}`);
}

export async function createNoteReply(data) {
  const replyId = `RPL-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  return apiPost('/note-replies', { reply_id: replyId, ...data });
}

export async function markNoteRead(noteId, email, name) {
  return apiPost('/note-reads', { note_id: noteId, user_email: email, user_name: name });
}

export async function loadNoteReads(noteId) {
  return apiGet(`/note-reads?note_id=${encodeURIComponent(noteId)}`);
}

export async function loadNoteActivity(noteId) {
  return apiGet(`/note-activity?note_id=${encodeURIComponent(noteId)}`);
}

// ===== MAP PRESETS =====

export async function loadMapPresets(projectId) {
  return apiGet(`/map-presets?project_id=${encodeURIComponent(projectId)}`);
}

export async function saveMapPreset(projectId, presetName, config, updatedBy) {
  return apiPost('/map-presets', { project_id: projectId, preset_name: presetName, config, updated_by: updatedBy });
}

// ===== GPS CREW TRACKING =====

// Local ping buffer — flushes every 5 pings or 60 seconds
let _pingBuffer = [];
let _pingFlushTimer = null;

async function _flushPings() {
  if (_pingBuffer.length === 0) return;
  const batch = _pingBuffer.splice(0, _pingBuffer.length);
  try {
    await apiPost('/worker-pings', { pings: batch });
  } catch (err) {
    console.error('[mapService] GPS ping upload failed:', err);
    // Put failed pings back (up to 100)
    _pingBuffer = [...batch, ..._pingBuffer].slice(0, 100);
  }
}

export function uploadGpsPing(ping) {
  const pingId = `PING-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  _pingBuffer.push({ ping_id: pingId, ...ping, timestamp: ping.timestamp || new Date().toISOString() });

  // Flush every 5 pings
  if (_pingBuffer.length >= 5) {
    _flushPings();
  }

  // Also flush on a 60s timer
  if (!_pingFlushTimer) {
    _pingFlushTimer = setInterval(() => {
      if (_pingBuffer.length > 0) _flushPings();
    }, 60000);
  }
}

export async function loadCrewLocations(projectId) {
  const params = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  return await apiGet(`/worker-locations${params}`);
}

// ===== EXPORTS =====

export { DB, SEGMENT_TABS, DEVICE_TABS, ALL_BILLING_TABS };

// v4.0.0 - Unified device storage into single Devices tab
// v5.0.0 - Added Map Tracer integration: publishTracerData, loadTracerFromSheets, loadProjectsList
// v5.1.0 - Centralized tab names (TAB constant), fixed Segments headers + column ranges (A:AJ), removed dead tabs
// v5.2.0 - Added change orders, invoices, damage reports, assignments extensions
// v6.0.0 - Per billing code tabs: each bore_code/unit_code gets its own sheet tab (SEGMENT_TABS, DEVICE_TABS)
// v7.0.0 - Postgres migration: projects/segments/devices/assignments/line-items → Vercel Postgres via /api/ routes
//          Sheets-only: rate cards, issues, locates, invoices, damage reports, change orders, work log
